import { Divider, Grid, Stack, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useState } from 'react';

import { TransitGroup, TransitHouseConsignment } from '@e-origin/shared';

import { MultiLangCodes, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { getAmendmentType } from '../../../../utils';
import { TransitDuplicateRow } from '../../../TransitDuplicateRow';
import { TransitAutocompleteHeader } from '../../TransitAutocompleteHeader';
import { TransitInputHeader } from '../../TransitInputHeader';
import { TransitViewHouseFormValues } from '../../validateTransitViewHouseForm';
import { TransitViewEditGoodsItemFormValues } from './validateTransitViewEditGoodsItemForm';

interface GoodsItemDataProps {
  form: UseFormReturnType<TransitViewHouseFormValues>;
  goodsItemForm: UseFormReturnType<TransitViewEditGoodsItemFormValues>;
  houseConsignment: TransitHouseConsignment;
  disabled: boolean;
  editDisabledConditionalForArrival: boolean;
}

export const GoodsItemData: React.FC<GoodsItemDataProps> = (props) => {
  const [showHSCodeError, setShowHSCodeError] = useState(false);

  const { form, goodsItemForm, houseConsignment, disabled, editDisabledConditionalForArrival } = props;
  const isArrival = form?.values?.generalInfo?.group === TransitGroup.ARRIVAL;
  const codelistSystems = useCodeListSystem({ isTransit: true });
  const { isGeneralAmendment, isGuaranteeAmendment } = getAmendmentType(form.values.TransitOperation.amendment);

  return (
    <Stack my="md">
      <Grid>
        <Grid.Col span={3}>
          <TransitAutocompleteHeader
            form={goodsItemForm}
            parents={[form?.values?.TransitOperation.declarationType]}
            name="declarationType"
            disabled={disabled || isGeneralAmendment || isGuaranteeAmendment}
            placeholder="Declaration Type"
            code="CL231"
          />
        </Grid.Col>
        {!isArrival && (
          <Grid.Col span={3}>
            <TransitAutocompleteHeader
              form={goodsItemForm}
              parents={[form?.values?.Consignment.countryOfDispatch, houseConsignment?.countryOfDispatch]}
              name="countryOfDispatch"
              disabled={disabled || isGeneralAmendment || isGuaranteeAmendment}
              placeholder="Country Of Dispatch"
              code="CL008"
            />
          </Grid.Col>
        )}
        <Grid.Col span={3}>
          <TransitAutocompleteHeader
            form={goodsItemForm}
            parents={[form?.values?.Consignment.countryOfDestination, houseConsignment?.countryOfDestination]}
            name="countryOfDestination"
            disabled={disabled || isGeneralAmendment || isGuaranteeAmendment}
            placeholder="Country Of Destination"
            code="CL008"
          />
        </Grid.Col>
        {!isArrival && (
          <Grid.Col span={3}>
            <TransitInputHeader
              form={goodsItemForm}
              parents={[form?.values?.Consignment.referenceNumberUCR, houseConsignment?.referenceNumberUCR]}
              name="referenceNumberUCR"
              label="Reference Number UCR"
              disabled={disabled || isGeneralAmendment || isGuaranteeAmendment}
            />
          </Grid.Col>
        )}
      </Grid>
      <Grid>
        <Grid.Col span={3}>
          <TextInput
            {...goodsItemForm.getInputProps('Commodity.descriptionOfGoods')}
            label="Description of goods"
            disabled={editDisabledConditionalForArrival || isGuaranteeAmendment}
            data-pw="description-of-goods-input"
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput
            {...goodsItemForm.getInputProps('Commodity.CommodityCode.hsCode')}
            value={goodsItemForm.values.Commodity.CommodityCode.hsCode}
            onChange={(event) => {
              const initial6Digits = goodsItemForm.values.Commodity.CommodityCode.hsCode.substring(0, 6);
              const new6Digits = event.currentTarget.value.substring(0, 6);

              if (isArrival && initial6Digits !== new6Digits) {
                setShowHSCodeError(true);
                setTimeout(() => setShowHSCodeError(false), 5000);
                return;
              }

              goodsItemForm.setFieldValue('Commodity.CommodityCode.hsCode', event.currentTarget.value);
            }}
            label="HS code"
            disabled={disabled || isGeneralAmendment || isGuaranteeAmendment}
            data-pw="hs-code-input"
          />
          {showHSCodeError && <Text c="red">First 6 digits can't be changed</Text>}
        </Grid.Col>
        <Grid.Col span={3}>
          <MultiLangCodes
            {...goodsItemForm.getInputProps('Commodity.CUSCode')}
            label="CUS code"
            code="CL016"
            system={codelistSystems}
            disabled={editDisabledConditionalForArrival || isGuaranteeAmendment}
          />
        </Grid.Col>
      </Grid>

      <Divider m="sm" />

      <TransitDuplicateRow
        addButtonLabel="Add UNNumber"
        addButtonDataPW="add-unnumber-btn"
        form={goodsItemForm}
        inputs={[
          {
            type: 'MultiLang',
            label: 'UNNumber',
            name: 'UNNumber',
            width: 25,
            code: 'UnDangerousGoodsCode',
          },
        ]}
        arrayPath="Commodity.DangerousGoods"
        disabled={disabled || isGuaranteeAmendment}
      />
      <Grid>
        <Grid.Col span={4}>
          <TextInput
            {...goodsItemForm.getInputProps('Commodity.GoodsMeasure.grossMass')}
            label="Gross mass"
            disabled={editDisabledConditionalForArrival || isGuaranteeAmendment}
            data-pw="gross-mass-input"
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            {...goodsItemForm.getInputProps('Commodity.GoodsMeasure.netMass')}
            label="Net mass"
            disabled={editDisabledConditionalForArrival || isGuaranteeAmendment}
          />
        </Grid.Col>
        {!isArrival && (
          <Grid.Col span={4}>
            <TextInput
              {...goodsItemForm.getInputProps('Commodity.GoodsMeasure.supplementaryUnits')}
              label="Supplementary Units"
              disabled={disabled || isGuaranteeAmendment}
            />
          </Grid.Col>
        )}
      </Grid>

      <Divider m="sm" />

      <TransitDuplicateRow
        addButtonLabel="Add package"
        addButtonDataPW="add-package-btn"
        form={goodsItemForm}
        inputs={[
          {
            type: 'MultiLang',
            label: 'Type of Packages',
            name: 'typeOfPackages',
            code: 'CL017',
            width: 20,
            'data-pw': 'type-of-packages-input',
          },
          {
            type: 'Input',
            label: 'Number of Packages',
            name: 'numberOfPackages',
            width: 24,
            'data-pw': 'number-of-packages-input',
          },
          {
            type: 'Input',
            label: 'Marks',
            name: 'shippingMarks',
            width: 36,
            'data-pw': 'shipping-marks-input',
          },
        ]}
        arrayPath="Packaging"
        disabled={editDisabledConditionalForArrival || isGuaranteeAmendment}
      />
      {!isArrival && (
        <Grid>
          <Grid.Col span={4}>
            <TextInput
              {...goodsItemForm.getInputProps('Commodity.CommodityCode.initialAmountToPayForDuties')}
              label="Initial Amount To Pay For Duties"
              disabled={disabled || isGuaranteeAmendment}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput
              {...goodsItemForm.getInputProps('Commodity.CommodityCode.initialAmountToPayForVat')}
              label="Initial Amount To Pay For VAT"
              disabled={disabled || isGuaranteeAmendment}
            />
          </Grid.Col>
        </Grid>
      )}
    </Stack>
  );
};
